import { useShallow } from "zustand/react/shallow";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { CAROUSEL_MAX_CHARS, checkErrorFromWord, getDefaultValueOfFlagAllowTracking, getValidationsFromInteractiveType, maxFooterChars, VARIABLE_REGEX } from "../../channels/global/whatsapp/templates/utils";
import { useRef } from "react";
import { createWhatsappTemplateButton, getWhatsappTemplateButtonSubTypeReponse, whatsappCarouselCardData } from "../../../../models/axiosModel";
import { useMediaQuery } from "@mui/material";
import { isInvalidUrl } from "../constant";

export const useCarouselFunctions = () => {

    const { createTemplatePayload, setCreateTemplatePayload, whatsappTemplateButtonGenericData, existingTemplateData, 
        whatsappTemplateTypes, interactiveTypeList, existingPayloadKey, setExistingPayloadKey
    } = useTemplateLibraryStore(useShallow((store) => ({
        createTemplatePayload: store.createTemplatePayload,
        setCreateTemplatePayload: store.setCreateTemplatePayload,
        whatsappTemplateButtonGenericData: store.whatsappTemplateButtonGenericData,
        existingTemplateData: store.existingTemplateData,
        whatsappTemplateTypes: store.whatsappTemplateTypes,
        interactiveTypeList: store.interactiveTypeList,
        existingPayloadKey: store.existingPayloadKey,
        setExistingPayloadKey: store.setExistingPayloadKey,
    })))

    const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
    const headerTextareaCarouselRef = useRef<HTMLTextAreaElement | null>(null);

    const isScreenBelow1600 = useMediaQuery('(min-width: 0px) and (max-width : 1600px)');
    const isScreenBelow1250 = useMediaQuery("(max-width: 1250px) and (min-width: 0px)");
    const isScreenBelow1000 = useMediaQuery("(max-width: 1000px) and (min-width: 0px)");
    const isMobileScreen = useMediaQuery("(max-width: 600px) and (min-width: 0px)");

    // Message Body Text
    const handleFormattingCarousel = (format: 'bold' | 'italic' | 'strikethrough' | 'monospace', type: string, bodyIndex?: number) => {
        if (createTemplatePayload?.whatsapp_template_id) return;
        if (
            !textareaRefs.current?.[bodyIndex || 0] &&
            type.toLowerCase() === "body") return;
        if (
            !headerTextareaCarouselRef.current &&
            type.toLowerCase() === "header") return;


        const textarea = type.toLowerCase() === "body" ? textareaRefs.current?.[bodyIndex || 0] : headerTextareaCarouselRef.current;
        const value = type.toLowerCase() === "body" ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_value : createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header;
        const start = (textarea as HTMLTextAreaElement).selectionStart;
        const end = (textarea as HTMLTextAreaElement).selectionEnd;

        let formattedText = '';
        let cursorAdjustment = 0;

        switch (format) {
            case 'bold':
                formattedText = `*${value.substring(start, end)}*`;
                cursorAdjustment = 1;
                break;
            case 'italic':
                formattedText = `_${value.substring(start, end)}_`;
                cursorAdjustment = 1;
                break;
            case 'strikethrough':
                formattedText = `~${value.substring(start, end)}~`;
                cursorAdjustment = 1;
                break;
            // case 'monospace':
            //     formattedText = `\`\`\`${value.substring(start, end)}\`\`\``;
            //     cursorAdjustment = 3;
            //     break;
            default:
                formattedText = value.substring(start, end);
        }

        const newValue = value.substring(0, start) + formattedText + value.substring(end);

        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, index: number) => {
                            if (index === (bodyIndex || 0)) {
                                return {
                                    ...card,
                                    whatsapp_template_body: {
                                        ...card?.whatsapp_template_body,
                                        whatsapp_template_body_value: newValue
                                    }
                                }
                            }
                            return card;
                        })
                    }
                }
            });
        }

        else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_header: newValue
                    }
                }
            });
        }

        // Reset cursor position
        setTimeout(() => {
            (textarea as HTMLTextAreaElement).setSelectionRange(start + cursorAdjustment, start + cursorAdjustment);
            (textarea as HTMLTextAreaElement).focus();
        }, 0);
    };

    const handleAddCarouselVariableManually = (value: string, type: string, bodyIndex?: number) => {
        let bodyVariableData = type.toLowerCase() === "body"
            ? [...(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [])]
            : [...(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable ?? [])]
        
        let headerVaribaleData = [...(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data ?? [])]
        let replacementString = `{{${bodyVariableData.length + 1}}}`
        
        const newValue = value.slice(0, -2) + replacementString;
        const newFieldId = bodyVariableData.length + 1;
        let fieldObj = {
            // "whatsapp_template_dynamic_variable_id": getExistingVariableDataId(`{{${newFieldId}}}`, type),
            "whatsapp_template_dynamic_variable_key": `{{${newFieldId}}}`,
            "whatsapp_template_dynamic_variable_sample_value": ""
        }

        //@ts-ignore
        bodyVariableData.push(fieldObj);

        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, index: number) => {
                            if (index == (bodyIndex || 0)) {
                                return {
                                    ...card,
                                    whatsapp_template_body: {
                                        ...card.whatsapp_template_body,
                                        whatsapp_template_body_value: newValue,
                                        whatsapp_template_body_variable_data: bodyVariableData
                                    }
                                }
                            }
                            return card;
                        })
                    }
                },
            });
        } 
        else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data.whatsapp_template_carousel,
                        whatsapp_template_carousel_header: newValue,
                        whatsapp_template_carousel_header_dynamic_variable: bodyVariableData
                    }
                },
            });
        }
    }

    const removeCarouselVariableData = (value: string, type: string, bodyIndex?: number) => {
        interface whatsappTemplateBodyVariableDataNew {
            whatsapp_template_dynamic_variable_id: number;
            whatsapp_template_dynamic_variable_key: string;
            whatsapp_template_dynamic_variable_sample_value: string;
        }

        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const regex: RegExp = VARIABLE_REGEX['FLAG_NUMBER'];
        const matches: string[] | null = value?.match(regex);
        let val = value
        const uniqueMatches: string[] = matches ? Array.from(new Set(matches)) : [];
        
        let variableData = type?.toLowerCase() === "body"
            ? [...(latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [])]
            : [...(latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable ?? [])];
        
        uniqueMatches?.map((x: any, index: number) => {

            let current = Number((x.replaceAll('{{', '')).replaceAll('}}', ''))
            if (current > 1) {
                let prevIndex = Number((uniqueMatches[index - 1]?.replaceAll('{{', ''))?.replaceAll('}}', ''))
                if (current === prevIndex + 1) {
                } else {
                    val = val?.replaceAll(`{{${current}}}`, `{{${current - 1}}}`)
                }
            }
            return val
        })

        const result = uniqueMatches.map((key, index) => ({
            // whatsapp_template_dynamic_variable_id: getExistingVariableDataId(key, type),          
            whatsapp_template_dynamic_variable_key: `{{${index + 1}}}`,
            whatsapp_template_dynamic_variable_sample_value: type?.toLowerCase() === "body" ?
                latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data?.find((data: any) => data.whatsapp_template_dynamic_variable_key === key)?.whatsapp_template_dynamic_variable_sample_value ?? ""
                :
            latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable?.find((data: any) => data?.whatsapp_template_dynamic_variable_key === key)?.whatsapp_template_dynamic_variable_sample_value ?? ""
        }))
        let finalValue = {
            textAreaValue: value,
            variableData: variableData
        }
        if (uniqueMatches.length > 0) {
            finalValue.textAreaValue = val
            //@ts-ignore
            finalValue.variableData = result
        } else {
            finalValue.variableData = []
        }
        return finalValue
        // console.log(result);
        // setCreateTemplatePayload((prevState:any) => ({ ...prevState, whatsapp_template_data: { ...prevState.whatsapp_template_data, whatsapp_template_body: { ...prevState.whatsapp_template_data.whatsapp_template_body, whatsapp_template_body_variable_data: result } } }))
    }

    const checkFlagErrorCarousel = (type: string, bodyIndex?: number) => {
        let wordForBody = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[bodyIndex ?? 0]?.whatsapp_template_body?.whatsapp_template_body_value;
        let wordForHeader = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header || "";
        
        if (type.toLowerCase() === "body") {
            return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter);
        }
        else if (type.toLowerCase() === "header") {
            return checkErrorFromWord(wordForHeader, createTemplatePayload?.flag_named_parameter);
        }
        else if (type.toLowerCase() === "both") {
            if (wordForHeader) return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter) || checkErrorFromWord(wordForHeader, createTemplatePayload?.flag_named_parameter);
            return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter);
        }
    }

    const handleAddCarouselField = (type: string, insertVariable: (variableKey: string, msgIndex: number | string, msgContentBeforeInsert: string) => string, bodyIndex?: number) => {
        let fields: any = type.toLowerCase() === "body" ?
            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data
            :
            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable
        
        const newFieldId = fields?.length + 1;

        let fieldObj = {
            // "whatsapp_template_dynamic_variable_id": getExistingVariableDataId(`{{${newFieldId}}}`, type),
            "whatsapp_template_dynamic_variable_key": `{{${newFieldId}}}`,
            "whatsapp_template_dynamic_variable_sample_value": ""
        }
        fields?.push(fieldObj)

        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, index: number) => {
                            if (index === (bodyIndex || 0)) {
                                return {
                                    ...card,
                                    whatsapp_template_body: {
                                        ...card.whatsapp_template_body,
                                        whatsapp_template_body_value: insertVariable(String(newFieldId), `body${index}`, card?.whatsapp_template_body?.whatsapp_template_body_value ?? ''),
                                        whatsapp_template_body_variable_data: fields
                                    }
                                }
                            }
                            return card;
                        })
                    }
                }
            });
        } else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_header: insertVariable(String(newFieldId), 'header', createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? ''),
                        whatsapp_template_carousel_header_dynamic_variable: fields
                    }
                }
            });
        }
    }

    const checkDisableAddVarButtonCarousel = () => {
        const fields = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable;
        if (fields?.length >= 10) {
            return true;
        }
        return false;
    }

    const handleChangeCarouselVar = (id: any, event: any, type: string, bodyIndex?: number) => {
        let fields = type.toLowerCase() === "body" ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data : createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable
        
        const newFields = fields?.map((field: any) => {
            if (field.whatsapp_template_dynamic_variable_key === id) {
                return { ...field, whatsapp_template_dynamic_variable_sample_value: event.target.value };
            }
            return field;
        });

        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, index: number) => {
                            if (index === (bodyIndex || 0)) {
                                return {
                                    ...card,
                                    whatsapp_template_body: {
                                        ...card?.whatsapp_template_body,
                                        whatsapp_template_body_variable_data: newFields
                                    }
                                }
                            }
                            return card;
                        })
                    }
                }
            });
        } else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_header_dynamic_variable: newFields
                    }
                }
            });
        }
    };

    const handleRemoveCarouselField = (id: any, type: string, bodyIndex?: number) => {
        let fields: any = type.toLowerCase() === "body" ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_variable_data : createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable
        let newFields = fields?.filter((field: any) => field.whatsapp_template_dynamic_variable_key !== id)
        
        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, index: number) => {
                            if (index === (bodyIndex || 0)) {
                                return {
                                    ...card,
                                    whatsapp_template_body: {
                                        ...card?.whatsapp_template_body,
                                        whatsapp_template_body_value: `${card.whatsapp_template_body.whatsapp_template_body_value.replaceAll(`${id}`, '').trim()}`,
                                        whatsapp_template_body_variable_data: newFields
                                    }
                                }
                            }
                            return card;
                        })
                    }
                }
            });
        } else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_header: `${createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header?.replaceAll(`${id}`, '').trim()}`,
                        whatsapp_template_carousel_header_dynamic_variable: newFields
                    }
                }
            });
        }
    };

    const updateCarouselMessageBodyText = (value: string, handleChangeText: (event: any, type: string) => void, cursorPosition?: number) => {
        if (value.endsWith('{{')) {
            handleAddCarouselVariableManually(value, "header");
            return;
        }

        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const transformedValue = value.replace(VARIABLE_REGEX['FLAG_NUMBER'], '*');

        if (transformedValue.length > CAROUSEL_MAX_CHARS?.HEADER_TEXT) {
            return;
        }

        const finalValues = removeCarouselVariableData(value, "header");
        handleChangeText({ target: { value } }, 'carouselheader');
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...latestPayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_header: finalValues?.textAreaValue,
                    whatsapp_template_carousel_header_dynamic_variable: finalValues?.variableData,
                },
            },
        });

        if (cursorPosition !== undefined && headerTextareaCarouselRef.current) {
            requestAnimationFrame(() => {
                headerTextareaCarouselRef.current?.focus();
                headerTextareaCarouselRef.current?.setSelectionRange(cursorPosition, cursorPosition);
            });
        }
    };

    const handleCarouselMessageBodyTextChange = (event: any, handleChangeText: (event: any, type: string) => void) => {
        updateCarouselMessageBodyText(event.target.value, handleChangeText);
    }

    // Buttons
    const checkDisableAddBtnForCarousel = (): boolean => {
        const totalButtons = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0]?.whatsapp_template_button?.reduce((acc: any, item: any) => {
            return acc += item.whatsapp_template_button_data.length;
        }, 0);
        if (totalButtons >= 2) {
            return true;
        }
        else return false;
    }

    const reOrderButtonFieldForCustomBtn = (selectedSubButton: getWhatsappTemplateButtonSubTypeReponse) => {
        const buttonDescriptionField = [];
        const payloadField = [];
        const otherFields = [];

        for (const item of selectedSubButton.whatsapp_template_button_sub_type_field_data) {
            const fieldName = item.field_name.toLowerCase();
            switch (fieldName) {
                case "button description":
                    buttonDescriptionField.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: "",
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
                case "payload":
                    payloadField.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: [],
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
                default:
                    otherFields.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: "",
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
            }
        }

        if (createTemplatePayload?.whatsapp_template_flag_interactive_message && getValidationsFromInteractiveType(createTemplatePayload?.interactive_type_id, interactiveTypeList)?.show_btn_description_field) {
            const reOrderedArray = [...otherFields, ...buttonDescriptionField, ...payloadField];
            return reOrderedArray;
        } else {
            return [...otherFields, ...payloadField];
        }
    }

    const getExistingActionButtonId = (buttonTypeId: number) => {
        let existingButton = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0]?.whatsapp_template_button?.find((button: any) => Number(button?.whatsapp_template_button_type_id) === buttonTypeId)
        if (existingButton) {
            let buttonId = existingButton?.whatsapp_template_button_id
            return buttonId
        } else {
            return null
        }
    }

    const getExistingActionButtonDataId = (buttonTypeId: number, subTypeId: number, index?: number) => {
        let existingButton = existingTemplateData?.whatsapp_template_data?.whatsapp_template_button?.find((button) => Number(button?.whatsapp_template_button_type_id) === buttonTypeId)
        if (existingButton) {
            let subButtonData = existingButton?.whatsapp_template_button_data.filter((subBtn) => subBtn?.whatsapp_template_button_sub_type_id === subTypeId)
            if (subButtonData && subButtonData.length > 0)
                var currentSubBtnData = [];
            let currentButtonData = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.find((btn) => Number(btn?.whatsapp_template_button_type_id) === buttonTypeId)
            let subBtnId = null
            if (index !== undefined) {
                let subBtndata = subButtonData[index]
                if (subBtndata && subBtndata?.whatsapp_template_button_data_id) {
                    subBtnId = subButtonData[index]?.whatsapp_template_button_data_id
                } else {
                    subBtnId = subButtonData[0]?.whatsapp_template_button_data_id ?? null
                }
            } else {
                if (currentButtonData) {
                    currentSubBtnData = currentButtonData?.whatsapp_template_button_data?.filter((subBtn) => subBtn?.whatsapp_template_button_sub_type_id == subTypeId)
                    let subBtndata = subButtonData[currentSubBtnData.length]
                    if (subBtndata) {
                        subBtnId = subButtonData[currentSubBtnData.length]?.whatsapp_template_button_data_id
                    }
                } else {
                    subBtnId = subButtonData[0]?.whatsapp_template_button_data_id
                }
            }
            return subBtnId
        } else {
            return null
        }
    }

    const getNewBtnType = (buttonTypeId: number, subTypeId: number) => {
        const selectedSubButton = whatsappTemplateButtonGenericData?.find((btn: any) => btn?.whatsapp_template_button_type_id === buttonTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType?.whatsapp_template_button_sub_type_id === subTypeId);
        const isCustom = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "custom";
        const isVisitWebsiteButton = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";

        let reorderedFields = selectedSubButton?.whatsapp_template_button_sub_type_field_data?.map((field) => ({
            ...field,
            "whatsapp_template_button_sub_type_field_value":
                field?.field_name?.toLowerCase() === "payload"
                    ? []
                    : (isVisitWebsiteButton && field?.field_name?.toLowerCase() === "website url" ? "https://" : field?.field_static_value_flag ? field?.field_static_value : ""),
            "whatsapp_template_button_sub_type_field_option_id": null,
            ...(isVisitWebsiteButton && field?.field_name?.toLowerCase() === "url type" ? {
                "whatsapp_template_button_sub_type_field_value": "Static",
                "whatsapp_template_button_sub_type_field_option_id": field?.field_option_data?.find(
                    (option: any) => option?.field_option_value?.toLowerCase() === "static"
                )?.field_option_id ?? 2,
            } : {})
        }));
        if (selectedSubButton && isCustom) {
            reorderedFields = reOrderButtonFieldForCustomBtn(selectedSubButton);
        }
        const newButtonType = {
            "whatsapp_template_button_id": getExistingActionButtonId(buttonTypeId),
            "flag_allow_tracking": getDefaultValueOfFlagAllowTracking(selectedSubButton?.whatsapp_template_button_sub_type_name, whatsappTemplateTypes?.find((types) => types.whatsapp_template_type_id === createTemplatePayload?.whatsapp_template_type_id)?.name || ""),
            "whatsapp_template_button_type_id": buttonTypeId,
            "whatsapp_template_button_data": [
                {
                    "whatsapp_template_button_data_id": getExistingActionButtonDataId(buttonTypeId, subTypeId),
                    "whatsapp_template_button_sub_type_id": subTypeId,
                    "whatsapp_template_button_sub_type_name": selectedSubButton?.whatsapp_template_button_sub_type_name,
                    "whatsapp_template_button_sub_type_field_data": reorderedFields,
                }
            ]
        };
        return newButtonType;
    }

    const addButtonWithSubTypeGrouping = (existingButtons: createWhatsappTemplateButton[], newButton: ReturnType<typeof getNewBtnType>, subTypeId: number) => {
        const lastIndexOfNewBtn = existingButtons?.map((btn: createWhatsappTemplateButton) => btn?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id)?.lastIndexOf(subTypeId);
        const btnsBeforeNewBtn = existingButtons?.slice(0, lastIndexOfNewBtn + 1) ?? [];
        const btnsAfterNewBtn = existingButtons?.slice(lastIndexOfNewBtn + 1) ?? [];

        // If same subTypeId exists, insert after last occurance
        if(lastIndexOfNewBtn !== -1){
            // We need to group buttons that share the same sub-type ID; otherwise, Meta will return an error.
            return [
                ...btnsBeforeNewBtn, 
                newButton,
                ...btnsAfterNewBtn
            ];
        }

        // If no matching subTypeId, append at the end
        return [...existingButtons, newButton];
    }

    const handleAddActionsButtonForCarousel = (buttonTypeId: number, subTypeId: number) => {
        const newButtonType = getNewBtnType(buttonTypeId, subTypeId);
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any) => {
                        return {
                            ...card,
                            whatsapp_template_button: addButtonWithSubTypeGrouping(card.whatsapp_template_button, newButtonType, subTypeId)
                        }
                    })
                }
            }
        });
    }

    const flagShowPayloadButton = (field: any, action: any) => {
        const selected_btn_type = whatsappTemplateButtonGenericData?.find((btn: any) => btn?.whatsapp_template_button_type_id === action?.whatsapp_template_button_type_id);
        if (selected_btn_type) {
            const selected_sub_btn_type = selected_btn_type?.whatsapp_template_button_sub_type_data?.find((btnSubType: any) => btnSubType?.whatsapp_template_button_sub_type_id === field?.whatsapp_template_button_sub_type_id);
            if (selected_sub_btn_type) {
                return selected_sub_btn_type?.whatsapp_template_button_sub_type_name?.toLowerCase() === "custom";
            }
        }
        return false;
    }

    const handleDeleteActionBtnForCarousel = (buttonTypeId: number, subButtonId: number, whatsappTemplateButtonDataIndex: number, index: number, inValidIdRefCarousel: any, carouselInvalidId: any, setCarouselInvalidId: (id: any) => void) => {
    
        setExistingPayloadKey(existingPayloadKey?.filter((payload: any) => payload?.whatsappTemplateButtonIndex !== index) ?? [])

        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, cardIndex: number) => {
                        
                        const updatedButtons = [...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[cardIndex]?.whatsapp_template_button];
                        let filteredActionButton = updatedButtons.filter((btn: any, ind: number) => ind !== index);
                        
                        return {
                            ...card,
                            whatsapp_template_button: filteredActionButton
                        }
                    })
                }
            }
        });

        // let carouselData = carouselInvalidId?.find((ids: any) => ids.index == cardIndex)
        carouselInvalidId?.map((ids: any) => {
            if (ids?.inValidIds?.includes(String(index + whatsappTemplateButtonDataIndex))) {
                setCarouselInvalidId(
                    carouselInvalidId?.map((carousel: any) => {
                        if (carousel?.index === ids?.index) {
                            return {
                                ...carousel,
                                inValidIds: carousel.inValidIds.filter((ele: any) => ele !== String(index + whatsappTemplateButtonDataIndex))
                            }
                        }
                        return carousel
                    })
                )
                inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC: any) => {
                    if (refC?.index === ids?.index) {
                        return {
                            ...refC,
                            inValids: refC?.inValids?.filter((ele: any) => ele !== String(index + whatsappTemplateButtonDataIndex))
                        }
                    }
                    return refC
                })
            }
            if (ids?.inValidIds?.includes(whatsappTemplateButtonDataIndex)) {
                setCarouselInvalidId(
                    carouselInvalidId?.map((carousel: any) => {
                        if (carousel?.index === ids?.index) {
                            return {
                                ...carousel,
                                inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== whatsappTemplateButtonDataIndex)
                            }
                        }
                        return carousel
                    })
                )
                inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC: any) => {
                    if (refC?.index === ids?.index) {
                        return {
                            ...refC,
                            inValids: refC?.inValids?.filter((ele: any) => ele !== whatsappTemplateButtonDataIndex)
                        }
                    }
                    return refC
                })
            }
        })


        // if (inValidId.includes(String(index + whatsappTemplateButtonDataIndex))) {
        //     setInvalidId(inValidId.filter((ele: any) => ele != String(index + whatsappTemplateButtonDataIndex)))
        //     inValidIdRef.current = inValidIdRef.current.filter((ele: any) => ele != String(index + whatsappTemplateButtonDataIndex));
        // }
        // if (inValidId.includes(whatsappTemplateButtonDataIndex)) {
        //     setInvalidId(inValidId.filter((ele: any) => ele != whatsappTemplateButtonDataIndex));
        //     inValidIdRef.current = inValidIdRef.current.filter((ele: any) => ele != whatsappTemplateButtonDataIndex);
        // }
    }

    const setOnChangeOfPayloadObjectCarousel = (field_name: string, value: any, btnTypeId: number, finalIndex: number, payloadIndex: number, subTypeIndex: number, cardIndex: number, whatsappTemplateButtonIndex: number, flagRemove?: boolean) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, ind: number) => {
                        if (ind === cardIndex || field_name?.toLowerCase() === "payload_key" || flagRemove) {
                            return {
                                ...card,
                                whatsapp_template_button: card.whatsapp_template_button.map((btn: any, btnIndex: any) => {
                                    if (btn?.whatsapp_template_button_type_id === btnTypeId && btnIndex === whatsappTemplateButtonIndex) {
                                        return {
                                            ...btn,
                                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                                if (buttonDataIndex === finalIndex) {
                                                    return {
                                                        ...buttonData,
                                                        whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                                            if (field?.whatsapp_template_button_sub_type_field_id === subTypeIndex) {
                                                                return {
                                                                    ...field,
                                                                    whatsapp_template_button_sub_type_field_value: flagRemove ?
                                                                        (Array.isArray(field?.whatsapp_template_button_sub_type_field_value) ? field?.whatsapp_template_button_sub_type_field_value : JSON.parse(field?.whatsapp_template_button_sub_type_field_value))?.filter((payload: any) =>
                                                                            payload?.payload_key_id ? payload?.payload_key_id !== payloadIndex : payload?.whatsapp_template_button_field_data_id !== payloadIndex
                                                                        )
                                                                        :
                                                                        field?.whatsapp_template_button_sub_type_field_value?.map((payload: any) =>
                                                                            payload?.payload_key_id ? payload?.payload_key_id === payloadIndex
                                                                                ? { ...payload, [field_name]: value }
                                                                                : payload
                                                                                :
                                                                                payload?.whatsapp_template_button_field_data_id === payloadIndex
                                                                                    ? { ...payload, [field_name]: value }
                                                                                    : payload
                                                                        )
                                                                };
                                                            }
                                                            return field;
                                                        })
                                                    };
                                                }
                                                return buttonData;
                                            })
                                        };
                                    }
                                    return btn;
                                })
                            }
                        }
                        return card;
                    })
                }
            }
        });

        if (field_name === "payload_key") {
            const keyOccurrences: Record<
                string,
                { index: number; whatsappTemplateButtonIndex: number }[]
            > = {};

            const duplicateKeysWithIndexes: {
                key: string;
                index: number;
                whatsappTemplateButtonIndex: number;
            }[] = [];

            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0]?.whatsapp_template_button?.forEach((btn, index) => {
                if (btn?.whatsapp_template_button_type_id === btnTypeId && index === whatsappTemplateButtonIndex) {

                    btn?.whatsapp_template_button_data?.forEach((btnData: any, buttonDataIndex: number) => {
                        if (buttonDataIndex === finalIndex) {
                            btnData?.whatsapp_template_button_sub_type_field_data?.forEach((subType: any) => {
                                if (subType.field_name?.toLowerCase() === "payload" && subType.whatsapp_template_button_sub_type_field_id === subTypeIndex) {
                                    if (Array.isArray(subType.whatsapp_template_button_sub_type_field_value)) {
                                        subType.whatsapp_template_button_sub_type_field_value.forEach((payload: any, i: number) => {
                                            const key = i === cardIndex ? value : payload?.payload_key;
                                            if (key) {
                                                if (!keyOccurrences[key]) {
                                                    keyOccurrences[key] = [];
                                                }
                                                // keyOccurrences[key].push(i);
                                                keyOccurrences[key].push({
                                                    index: i,
                                                    whatsappTemplateButtonIndex: whatsappTemplateButtonIndex,
                                                });
                                            }
                                        });

                                        // Now extract duplicates
                                        Object.entries(keyOccurrences).forEach(([key, indexes]) => {
                                            if (indexes.length > 1) {
                                                indexes.forEach(({ index, whatsappTemplateButtonIndex }) => {
                                                    duplicateKeysWithIndexes.push({ key, index, whatsappTemplateButtonIndex });
                                                });
                                            }
                                        });
                                    }
                                }
                            });
                        }
                    })

                }
            });

            let payloadKeys = existingPayloadKey?.filter((payload) => payload?.whatsappTemplateButtonIndex !== whatsappTemplateButtonIndex) ?? [];
            setExistingPayloadKey([...payloadKeys, ...duplicateKeysWithIndexes])
        }
        if (flagRemove) {
            let otherPayloadKeys = existingPayloadKey?.filter((payload) => payload?.whatsappTemplateButtonIndex !== whatsappTemplateButtonIndex) ?? [];
            let currentButtonsPyloadKeys = existingPayloadKey?.filter((payload) => payload?.whatsappTemplateButtonIndex === whatsappTemplateButtonIndex) ?? [];

            setExistingPayloadKey([...otherPayloadKeys, ...currentButtonsPyloadKeys?.filter((payload) => payload.key !== value)]);
        }
    }

    // Carousel Cards
    const checkDisableAddBtnForCarouselMax10Cards = () => {
        return createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.length >= 10
    }

    const getExistingCardsPayload = (fCardButton: any) => {
        return fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_field_data?.find((subType: any) => 
            subType?.field_name?.toLowerCase() === "payload")?.whatsapp_template_button_sub_type_field_value?.map((fieldValue: any) => 
                ({...fieldValue, payload_key_value: "", payload_key_id: 0 })) || [];
    }

    const checkFlagErrorCarouselBody = (type: string, bodyIndex?: number) => {
        let wordForBody = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[bodyIndex || 0]?.whatsapp_template_body?.whatsapp_template_body_value;
        let wordForHeader = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header || "";
        
        if (type.toLowerCase() === "body") {
            return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter);
        }
        else if (type.toLowerCase() === "header") {
            return checkErrorFromWord(wordForHeader, createTemplatePayload?.flag_named_parameter);
        }
        else if (type.toLowerCase() === "both") {
            if (wordForHeader) return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter) || checkErrorFromWord(wordForHeader, createTemplatePayload?.flag_named_parameter);
            return checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter);
        }
    }

    const updateCarouselCardBodyText = (value: string, cardIndex: number, handleChangeText: (event: any, type: string) => void, cursorPosition?: number) => {
        
        if (value?.endsWith('{{')) {
            handleAddCarouselVariableManually(value, "body", cardIndex);
            return;
        }

        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const transformedValue = value?.replace(VARIABLE_REGEX['FLAG_NUMBER'], '*');

        if (transformedValue.length > CAROUSEL_MAX_CHARS?.BODY_TEXT) {
            return;
        }

        const finalValues = removeCarouselVariableData(value, "body", cardIndex);
        handleChangeText({ target: { value } }, 'format');
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...latestPayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((card: any, ind: number) => {
                        if (ind === cardIndex) {
                            return {
                                ...card,
                                whatsapp_template_body: {
                                    ...card.whatsapp_template_body,
                                    whatsapp_template_body_value: finalValues?.textAreaValue,
                                    whatsapp_template_body_variable_data: finalValues?.variableData,
                                },
                            };
                        }
                        return card;
                    }),
                },
            },
        });

        if (cursorPosition !== undefined && textareaRefs.current[cardIndex]) {
            requestAnimationFrame(() => {
                const textarea = textareaRefs.current[cardIndex];
                textarea?.focus();
                textarea?.setSelectionRange(cursorPosition, cursorPosition);
            });
        }
    };

    const handleCarouselCardBodyChange = (event: any, cardIndex: number, handleChangeText: (event: any, type: string) => void) => {
        updateCarouselCardBodyText(event.target.value, cardIndex, handleChangeText);
    }

    // Carousel Card Footer
    const handleCarouselCardFooterChange = (event: any, cardIndex: number, cardData: whatsappCarouselCardData, handleChangeText: (event: any, type: string) => void) => {
        if (event.target.value.length <= maxFooterChars) {
            handleChangeText(event, 'footer');
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                            if (ind === cardIndex) {
                                return {
                                    ...card,
                                    whatsapp_template_footer: {
                                        ...card.whatsapp_template_footer,
                                        whatsapp_template_footer_id: cardData?.whatsapp_template_footer?.whatsapp_template_footer_id ? cardData?.whatsapp_template_footer?.whatsapp_template_footer_id : 0,
                                        whatsapp_template_footer_value: event.target.value,
                                    }
                                }
                            }
                            return card;
                        })
                    }
                }
            })
        }
    }

    // Carousel Body Description
    const handleCarouselBodyDescriptionChange = (event: any, cardIndex: number) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                        if (ind === cardIndex) {
                            return {
                                ...card,
                                whatsapp_template_body: {
                                    ...card.whatsapp_template_body,
                                    whatsapp_template_body_description: event.target.value
                                }
                            }
                        }
                        return card;
                    })
                }
            }
        })
    }

    // Carousel Disable Functions
    const checkBodyValueVariableCard = () => {
        let finalValue = false;
        for (let i = 0; i < createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.length; i++) {
            let wordForBody = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[i]?.whatsapp_template_body?.whatsapp_template_body_value;
            if (checkErrorFromWord(wordForBody, createTemplatePayload?.flag_named_parameter) === true) {
                finalValue = true
            }
        }
        return finalValue
    }

    const checkButtonVariableCard = () => {
        for (let i = 0; i < createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.length; i++) {
            for (const button of createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card[i]?.whatsapp_template_button) {
                for (const buttonData of button?.whatsapp_template_button_data) {
                    if (buttonData?.whatsapp_template_button_sub_type_field_data) {
                        for (const fieldData of buttonData?.whatsapp_template_button_sub_type_field_data) {
                            if (fieldData?.field_static_value_flag === true) {
                                return false;
                            }
                            if ((fieldData?.field_name ?? '').toLowerCase() === "website url" && fieldData?.whatsapp_template_button_field_data_variable && fieldData?.whatsapp_template_button_field_data_variable.length > 0) {
                                if ((fieldData.whatsapp_template_button_field_data_variable?.[0]?.whatsapp_template_dynamic_variable_sample_value ?? '').trim().length === 0) {
                                    return true;
                                }
                            }
                            if ((fieldData?.field_name ?? '').toLowerCase() === "website url" && isInvalidUrl((fieldData?.whatsapp_template_button_sub_type_field_value as string) ?? '')) {
                                return true;
                            }
                            if (fieldData?.field_type?.toLowerCase() === "textfield" && (!fieldData?.whatsapp_template_button_sub_type_field_value || (typeof fieldData?.whatsapp_template_button_sub_type_field_value == "string" && fieldData?.whatsapp_template_button_sub_type_field_value?.trim() === ''))
                            ) {
                                if (fieldData?.field_name?.toLowerCase() !== "button description") {
                                    return true;
                                }
                            }
                            if (fieldData?.field_name?.toLowerCase() === "payload" && (typeof fieldData?.whatsapp_template_button_sub_type_field_value == "object" && fieldData?.whatsapp_template_button_sub_type_field_value?.filter((values: any) => values?.payload_key?.trim() == "" || values?.payload_key_value?.trim() == "")?.length > 0)
                            ) {
                                return true;
                            }
                            if (fieldData?.field_type?.toLowerCase() === "autocomplete" && (fieldData?.whatsapp_template_button_sub_type_field_option_id == null || Number(fieldData?.whatsapp_template_button_sub_type_field_option_id) === 0)
                            ) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
    }

    const checkHeaderCard = () => {
        if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header?.trim()?.length > 0) {
            if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable?.filter((variable) => variable?.whatsapp_template_dynamic_variable_sample_value?.trim() == "")?.length === 0) {
                return true
            }
            return false
        }
        return false
    }

    const checkBodyValueCards = () => {
        return createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.every((cards) => cards.whatsapp_template_body?.whatsapp_template_body_value?.trim()?.length > 0)
    }

    const checkBodyVariableCard = () => {
        return createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.every(
            (bCard) =>
                bCard.whatsapp_template_body?.whatsapp_template_body_variable_data?.every(
                    (variable) =>
                        typeof variable?.whatsapp_template_dynamic_variable_sample_value === "string" &&
                        variable?.whatsapp_template_dynamic_variable_sample_value?.trim() !== ""
                )
        );
    }

    const checkButtonLengthCard = () => {
        return createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.every((cards) => cards?.whatsapp_template_button?.length >= 1)
    }

    const checkIfHeaderDataIsFillOrNotCarousel = (carouselFiles: any) => {
        const cards = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [];
        const headerType = cards?.[0]?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase();

        if (headerType === "image" || headerType === "video") {
            return Boolean(
                Array.isArray(carouselFiles) &&
                carouselFiles.length > 0 &&
                carouselFiles.length === cards.length &&
                carouselFiles.every((cFiles: any) => (cFiles?.carousels?.length ?? 0) > 0)
            );
        }

        return false;
    }

    return {
        isScreenBelow1600,
        isScreenBelow1250,
        isScreenBelow1000,
        isMobileScreen,
        textareaRefs,
        headerTextareaCarouselRef,

        handleFormattingCarousel,
        handleAddCarouselVariableManually,
        removeCarouselVariableData,
        checkFlagErrorCarousel,
        handleAddCarouselField,
        checkDisableAddVarButtonCarousel,
        handleChangeCarouselVar,
        handleRemoveCarouselField,
        handleCarouselMessageBodyTextChange,
        updateCarouselMessageBodyText,
        updateCarouselCardBodyText,

        checkDisableAddBtnForCarousel,
        reOrderButtonFieldForCustomBtn,
        getNewBtnType,
        handleAddActionsButtonForCarousel,
        flagShowPayloadButton,
        handleDeleteActionBtnForCarousel,
        setOnChangeOfPayloadObjectCarousel,

        checkDisableAddBtnForCarouselMax10Cards,
        getExistingCardsPayload,
        checkFlagErrorCarouselBody,

        handleCarouselCardBodyChange,
        handleCarouselCardFooterChange,
        handleCarouselBodyDescriptionChange,

        checkBodyValueVariableCard,
        checkButtonVariableCard,
        checkHeaderCard,
        checkBodyValueCards,
        checkBodyVariableCard,
        checkButtonLengthCard,
        checkIfHeaderDataIsFillOrNotCarousel
    };
}